import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Components
import SelectLabel from '../select-label/select-label';

// Actions
import consoleActions from '../../containers/console/console-actions';

export const MOVE_SELECT_VALUE = 'move';
export const COPY_SELECT_VALUE = 'copy';
export const REMOVE_SELECT_VALUE = 'remove';
export const UNASSIGNED_VALUE = '0';
const EMPTY_SELECT_VALUE = '';

function MoveListBody(props) {
    const { i18n } = useLocalisation();

    const {
        userId,
        userGroupId,
        fullName,
        updateMoveStudentList,
        availableClasses,
        // State
        productId,
        currentClassId,
        // Action props
        getAddStudentClasses,
        isSelectAllInUse,
        setIsSelectAllInUse
    } = props;

    // Select variables
    const [actionDropdownValue, setActionDropdownValue] = useState(EMPTY_SELECT_VALUE);
    const [classDropdownValue, setClassDropdownValue] = useState(EMPTY_SELECT_VALUE);
    const [groupDropdownValue, setGroupDropdownValue] = useState(EMPTY_SELECT_VALUE);

    // Reset actions select if not "all Students" and "all student" is being used
    useEffect(() => {
        if (userId !== 0 && isSelectAllInUse) {
            setActionDropdownValue(EMPTY_SELECT_VALUE);
        }
    }, [isSelectAllInUse]);

    // DEFAULT VALUES
    const DEFAULT_CLASS_VALUE = {
        id: EMPTY_SELECT_VALUE,
        name: i18n._(t`Move Modal - select classes default`)
    };
    const DEFAULT_GROUP_VALUE = {
        id: EMPTY_SELECT_VALUE,
        name: i18n._(t`Move Modal - select groups default`)
    };

    // Select - dropdown values
    const actionsList = [
        { value: EMPTY_SELECT_VALUE, label: i18n._(t`Move Modal - select actions default`) },
        { value: MOVE_SELECT_VALUE, label: i18n._(t`Move Modal - select actions move`) },
        { value: COPY_SELECT_VALUE, label: i18n._(t`Move Modal - select actions copy`) },
        { value: REMOVE_SELECT_VALUE, label: i18n._(t`Move Modal - select actions remove`) }
    ];
    const [classesList, setClassesList] = useState([DEFAULT_CLASS_VALUE]);
    const [groupsList, setGroupsList] = useState([DEFAULT_GROUP_VALUE]);
    const [isGroupListDisabled, setIsGroupListDisabled] = useState(true);
    const isAllStudentsLine = userId === 0;

    // Handlers
    const selectHandlerActions = (e) => {
        const actionValue = e.target.value;
        setActionDropdownValue(actionValue);

        // In case user deals with "All students" select
        if (isAllStudentsLine) {
            setIsSelectAllInUse(true);
        }
        updateMoveStudentList({
            userId,
            actionType: actionValue,
            isForAllStudents: isAllStudentsLine
        });
    };

    const selectHandlerClasses = (e) => {
        const classValue = e.target.value;
        setClassDropdownValue(classValue);

        if (classValue === UNASSIGNED_VALUE) {
            updateMoveStudentList({
                userId,
                actionType: actionDropdownValue,
                actionProperties: {
                    destinationClassId: classValue,
                    destinationGroupId: userGroupId
                },
                isForAllStudents: isAllStudentsLine
            });
        }
    };
    const selectHandlerGroups = (e) => {
        const groupValue = e.target.value;
        setGroupDropdownValue(groupValue);
        updateMoveStudentList({
            userId,
            actionType: actionDropdownValue,
            actionProperties: {
                destinationClassId: classDropdownValue,
                destinationGroupId: groupValue
            },
            isForAllStudents: isAllStudentsLine
        });
    };

    // Fetch the group resources
    useEffect(() => {
        let isCancelled = false;
        const fetchStudentClasses = async () => {
            const newStudentClassData = await getAddStudentClasses({
                productId,
                classId: classDropdownValue
            });
            const {
                classGroups
            } = newStudentClassData;

            // To make the classgroups object suitable for select box input
            const revisedClassGroups = classGroups.map(item => ({ id: item.groupId, name: item.name }));
            if (!isCancelled && newStudentClassData) {
                // Remove the item that has the id of 0, which indicates "all groups"
                const filteredGroups = revisedClassGroups.filter(group => group.id !== 0);

                // Set the appropriate data for Student Management
                setGroupsList([
                    DEFAULT_GROUP_VALUE,
                    ...filteredGroups
                ]);
                setIsGroupListDisabled(false);
            }
        };

        // Reset the group dropdown value whenever the class value changes
        setGroupDropdownValue(EMPTY_SELECT_VALUE);

        // For the case when the value of the classDropdownValue is unassigned, disable the group list dropdown
        if (classDropdownValue === UNASSIGNED_VALUE) {
            setIsGroupListDisabled(true);
            setGroupsList([DEFAULT_GROUP_VALUE]);
        } else if (classDropdownValue !== EMPTY_SELECT_VALUE) {
            fetchStudentClasses();
        }

        return () => {
            isCancelled = true;
        };
    }, [classDropdownValue]);

    useEffect(() => {
        // Reset the class/group dropdown so that they have to go back and change their preferences
        setClassDropdownValue(EMPTY_SELECT_VALUE);
        setGroupDropdownValue(EMPTY_SELECT_VALUE);

        // To make the available classlist object suitable for select box input
        const revisedAvailableClasses = availableClasses.map(item => ({ id: item.classId, name: item.name }));
        // If it's either a MOVE or copy, change the class list
        switch (actionDropdownValue) {
            case MOVE_SELECT_VALUE: {
                /**
                 * MOVE must include the current class, as the user can move around the student in the
                 * current class instead of changing a group one-by-one
                 */
                const filteredAvailableClasses = revisedAvailableClasses.filter(item => (
                    item.id !== parseInt(UNASSIGNED_VALUE, 10)
                ));
                setClassesList([DEFAULT_CLASS_VALUE, ...filteredAvailableClasses]);
                break;
            }
            case COPY_SELECT_VALUE: {
                // COPY must NOT include the current class
                const filteredAvailableClasses = revisedAvailableClasses.filter(item => (
                    item.id !== currentClassId && item.id !== parseInt(UNASSIGNED_VALUE, 10)
                ));
                setClassesList([DEFAULT_CLASS_VALUE, ...filteredAvailableClasses]);
                break;
            }
            default:
        }
    }, [actionDropdownValue, availableClasses]);

    const additionalClassName = userId === 0 ? 'table__row--all-students' : '';
    const displayName = fullName === ', ' ? i18n._(t`Move Modal - For all students`) : fullName;
    return (
        <tr className={`table__row ${additionalClassName}`}>
            <td className="table__cell table__cell--name">
                <span title={displayName} className="text-ellipsis">{displayName}</span>
            </td>
            <td className="table__cell table__cell--actions">
                <SelectLabel
                    id={`move-actions-${userId}`}
                    srLabel={`${i18n._(t`Move Modal - select actions label`)} ${displayName}`}
                    listOfOptions={actionsList}
                    propertyName="value"
                    visibleProperty="label"
                    value={actionDropdownValue}
                    isSelectDisabled={userId !== 0 && isSelectAllInUse}
                    selectHandler={selectHandlerActions} />
            </td>
            {
                actionDropdownValue === REMOVE_SELECT_VALUE ? (
                    <td colSpan={2} className="table__cell table__cell--actions">
                        <Trans id={'Move Modal - select remove info'} />
                    </td>
                ) : (
                    <Fragment>
                        <td className="table__cell table__cell--actions table__cell--class-detail">
                            <SelectLabel
                                id={`move-classes-${userId}`}
                                srLabel={`${i18n._(t`Move Modal - select classes label`)} ${displayName}`}
                                listOfOptions={classesList}
                                propertyName="id"
                                visibleProperty="name"
                                value={classDropdownValue}
                                isSelectDisabled={actionDropdownValue === EMPTY_SELECT_VALUE}
                                selectHandler={selectHandlerClasses} />
                        </td>
                        <td className="table__cell table__cell--actions table__cell--class-detail">
                            <SelectLabel
                                id={`move-groups-${userId}`}
                                srLabel={`${i18n._(t`Move Modal - select groups label`)} ${displayName}`}
                                listOfOptions={groupsList}
                                propertyName="id"
                                visibleProperty="name"
                                value={groupDropdownValue}
                                isSelectDisabled={
                                    actionDropdownValue === EMPTY_SELECT_VALUE ||
                                    classDropdownValue === EMPTY_SELECT_VALUE ||
                                    isGroupListDisabled
                                }
                                selectHandler={selectHandlerGroups} />
                        </td>
                    </Fragment>
                )
            }
        </tr>
    );
}

MoveListBody.propTypes = {
    userId: PropTypes.number,
    userGroupId: PropTypes.number,
    fullName: PropTypes.string,
    updateMoveStudentList: PropTypes.func,
    availableClasses: PropTypes.array,
    // State
    productId: PropTypes.number,
    currentClassId: PropTypes.number,
    // Action functions
    getAddStudentClasses: PropTypes.func,
    isSelectAllInUse: PropTypes.bool,
    setIsSelectAllInUse: PropTypes.func
};

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from MessageModalCombinedReducers.
 */
function mapStateToProps(state) {
    const {
        productId,
        currentClassId
    } = state.consoleReducer;

    return {
        productId,
        currentClassId
    };
}

/*
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getAddStudentClasses: consoleActions.getAddStudentClasses,
        getAddStudentDetails: consoleActions.getAddStudentDetails,
        assignClassStudent: consoleActions.assignClassStudent
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(MoveListBody);



// WEBPACK FOOTER //
// ./components/students-lists/move-list-body.js